// A simple sound service using Web Audio API to avoid needing asset files.
class SoundService {
    private audioContext: AudioContext | null = null;
    private isEnabled = true;

    constructor() {
        // AudioContext must be created after a user gesture.
        // We'll initialize it on the first sound play request.
        const savedPref = localStorage.getItem('psm_sound_enabled');
        this.isEnabled = savedPref === null ? true : savedPref === 'true';
    }

    private initContext() {
        if (!this.audioContext) {
            try {
                this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            } catch (e) {
                console.error("Web Audio API is not supported in this browser.");
            }
        }
    }

    private playSound(type: OscillatorType, frequency: number, duration: number, volume: number) {
        if (!this.isEnabled) return;
        this.initContext();
        if (!this.audioContext || this.audioContext.state === 'suspended') {
            this.audioContext?.resume();
        }
        if (!this.audioContext) return;


        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.8, this.audioContext.currentTime + duration);


        gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.00001, this.audioContext.currentTime + duration);

        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);

        oscillator.start(this.audioContext.currentTime);
        oscillator.stop(this.audioContext.currentTime + duration);
    }
    
    public playKick() {
        if (!this.isEnabled) return;
        this.initContext();
        if (!this.audioContext || this.audioContext.state === 'suspended') {
            this.audioContext?.resume();
        }
        if (!this.audioContext) return;
        
        const now = this.audioContext.currentTime;
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();

        // A simple synthesized kick drum sound
        osc.type = 'sine';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(0.01, now + 0.15);

        gain.gain.setValueAtTime(0.4, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

        osc.connect(gain);
        gain.connect(this.audioContext.destination);
        osc.start(now);
        osc.stop(now + 0.15);
    }

    public playClick() {
        this.playSound('triangle', 300, 0.1, 0.1);
    }

    public playNavigate() {
        this.playSound('sine', 440, 0.1, 0.15);
    }
    
    public playSuccess() {
        this.playSound('sine', 600, 0.2, 0.2);
        setTimeout(() => this.playSound('sine', 800, 0.2, 0.2), 100);
    }
    
    public playError() {
        this.playSound('square', 200, 0.3, 0.2);
    }

    public playAdvance() {
        this.playSound('sawtooth', 500, 0.3, 0.1);
    }
    
    public playGoal() {
        this.playSound('sine', 880, 0.2, 0.3);
        setTimeout(() => this.playSound('sine', 1046, 0.3, 0.3), 150);
    }

    public playWhistle() {
        this.playSound('sine', 2000, 0.5, 0.2);
    }

    public playCard() {
        this.playSound('square', 1500, 0.2, 0.15);
    }

    public toggleSound(enabled: boolean) {
        this.isEnabled = enabled;
        localStorage.setItem('psm_sound_enabled', String(enabled));
        if (enabled) {
            this.initContext();
        }
    }

    public getIsEnabled() {
        return this.isEnabled;
    }
}

export const soundService = new SoundService();